import { Form, Formik } from 'formik';
import React from 'react';
import ModalComponent from '../../components/modal/modal';
import ModalBodyComponent from '../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../components/modal/modalFooterComponent';
import InputText from '../../components/input-text/inputText';
import {
  useGetProviderTypes,
  useWealthlaneCustomerApi,
  useWealthlaneIdentityApi,
} from '../../hooks';
import _ from 'lodash';
import * as Yup from 'yup';
import Toggle from '../../components/toggle';
import usePostCompanySso from '../../hooks/post/usePostCompanySso';
import { TextArea } from '../../components/text-area/textArea.styled';
import CustomTextArea from '../../components/text-area/textArea';
import FormSelect from '../../components/dropdown/formSelect';
import { ModalStatusType } from '../../types/common';
import InitialValuesForm from '../../constants/initialValues';
import clsx from 'clsx';
import { SSO_CONFIG_VALIDATION } from '../../utils/validations/validations';
import useGetCompanySsoConfigById from '../../hooks/get/useGetCompanySsoConfigById';
import usePutCompanySso from '../../hooks/put/usePutCompanySso';
import { toast } from 'react-toastify';
import ToastComponent from '../../components/toast';
import Button from '../../components/button';
import useGetNewSSOSetup from '../../hooks/get/useGetNewSSOSetup';
import useGetPPTIdentifierTypes from '../../hooks/get/useGetPPTIdentifierTypes';

interface SsoConfigurationModalProps {
  id: string;
  modalStatus: ModalStatusType;
  isModalOpen: boolean;
  rowData: any;
  closeModal: () => void;
  onEditModal: () => void;
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
}

interface SsoConfigType {
  id?: string;
  companyId: string;
  providerName: string;
  providerCode: string;
  clientId: string;
  clientSecret: string;
  authority: string;
  redirectUri: string;
  postLogoutRedirectUri?: string;
  isEnabled: boolean;
  providerType: string;
  providerSigningCertificate: string;
  displayName: string;
}

const SsoConfigurationModal = ({
  id,
  modalStatus,
  isModalOpen,
  closeModal,
  onEditModal,
  rowData,
  handleRequest,
}: SsoConfigurationModalProps): JSX.Element => {
  const { handleRequest: handleRequestIdentity } = useWealthlaneIdentityApi();

  const { data: companySsoConfig, isLoading: isLoadingCompanySsoConfig } =
    useGetCompanySsoConfigById(handleRequestIdentity, id);

  const { data: providerTypes, isLoading: isLoadingProviderTypes } =
    useGetProviderTypes(handleRequestIdentity);

  const { data: pptIdentifierTypes, isLoading: isLoadingPPTIdentifierTypes } =
    useGetPPTIdentifierTypes(handleRequestIdentity);

  const { data: newSSOSetup, isLoading: isLoadingNewSSOSetup } = useGetNewSSOSetup(
    handleRequestIdentity,
    modalStatus === 'Add'
  );

  const { mutate: postCompanySso, isLoading: isLoadingPostCompanySso } = usePostCompanySso(
    handleRequestIdentity,
    () => {
      toast.success(
        <ToastComponent label='Success' message='SSO Configuration created successfully.' />
      );
      closeModal();
    }
  );
  const { mutate: putCompanySso, isLoading: isLoadingPutCompanySso } = usePutCompanySso(
    handleRequestIdentity,
    {
      id,
      onSuccess: () => {
        toast.success(<ToastComponent label='Success' message='SSO Configuration edited.' />);
        closeModal();
      },
    }
  );

  const handleProviderTypeChange = (value: string, setFieldValue: any): void => {
    setFieldValue('providerType', value);
  };

  const handleSubmit = (values: SsoConfigType): void => {
    if (modalStatus === 'Add') {
      postCompanySso(values);
    } else {
      putCompanySso(values);
    }
  };

  const onCopyToClipboard = (value: string): void => {
    navigator.clipboard.writeText(value);
    toast.success(<ToastComponent label='Success' message='Copied to clipboard.' />, {
      toastId: 99,
    });
  };

  const isEditMode = modalStatus === 'Edit';
  const isLoading = isLoadingCompanySsoConfig;

  return (
    <Formik
      enableReinitialize
      initialValues={
        companySsoConfig || isEditMode
          ? companySsoConfig
          : Object.assign(InitialValuesForm.ssoConfiguration, newSSOSetup)
      }
      validationSchema={SSO_CONFIG_VALIDATION(isEditMode)}
      onSubmit={handleSubmit}>
      {({ errors, values, touched, handleChange, setFieldValue, initialValues }) => {
        const isSAMLAuth = values?.providerType === 'SAML';
        const isProviderTypeSelected = !_.isEmpty(values?.providerType);
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            loading={isLoading}
            title={`${modalStatus === 'Add' ? 'Create' : modalStatus} SSO Configuration`}
            valueEdited={_.isEqual(values, initialValues)}
            size='md'
            children={
              <Form>
                <ModalBodyComponent>
                  <div className='grid grid-cols-7 gap-4'>
                    <FormSelect
                      containerClassName={clsx(
                        isProviderTypeSelected ? 'col-span-5' : 'col-span-7'
                      )}
                      label='Provider Type'
                      name='providerType'
                      onChange={(e: string) => {
                        handleProviderTypeChange(e, setFieldValue);
                      }}
                      errors={errors}
                      touched={touched}
                      options={providerTypes}
                      values={values}
                      arrValue={values?.providerType}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      isLoading={isLoadingProviderTypes}
                      disabled={modalStatus === 'View'}
                      required
                    />
                    {isProviderTypeSelected && (
                      <div className='col-span-2 flex justify-center items-center'>
                        <Toggle
                          id='isEnabled'
                          name='isEnabled'
                          label='Enabled'
                          onChange={() => setFieldValue('isEnabled', !values.isEnabled)}
                          checked={values.isEnabled}
                          disabled={modalStatus === 'View'}
                        />
                      </div>
                    )}
                  </div>
                  <InputText
                    label='Display Name'
                    name='displayName'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    required
                    disabled={modalStatus === 'View'}
                  />
                  <InputText
                    label='Provider Name'
                    name='providerName'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    required
                    disabled={modalStatus === 'View'}
                  />
                  {/* <InputText
                        label='Provider Code'
                        name='providerCode'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        required
                      /> */}
                  <InputText
                    label='Authority'
                    name='authority'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    required
                    disabled={modalStatus === 'View'}
                  />
                  {/* <InputText
                    label='Ppt Identifier Type'
                    name='claimType'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    required
                    disabled={modalStatus === 'View'}
                  /> */}
                  <FormSelect
                    containerClassName={clsx(isProviderTypeSelected ? 'col-span-5' : 'col-span-7')}
                    label='Ppt Identifier Type'
                    name='claimType'
                    onChange={(e: string) => {
                      setFieldValue('claimType', e);
                    }}
                    errors={errors}
                    touched={touched}
                    options={pptIdentifierTypes}
                    values={values}
                    arrValue={values?.claimType}
                    setFieldValue={setFieldValue}
                    inputType={modalStatus}
                    isLoading={isLoadingPPTIdentifierTypes}
                    disabled={modalStatus === 'View'}
                    required
                  />
                  <InputText
                    label='Ppt Identifier Name'
                    name='userMappingClaim'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    required
                    disabled={modalStatus === 'View'}
                  />

                  {isProviderTypeSelected && (
                    <>
                      {isProviderTypeSelected && !isSAMLAuth && (
                        <>
                          <InputText
                            label='Client ID'
                            name='clientId'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required
                            disabled={modalStatus === 'View'}
                          />
                          <CustomTextArea
                            label='Client Secret'
                            name='clientSecret'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            rows='3'
                            required
                            disabled={modalStatus === 'View'}
                          />
                        </>
                      )}
                    </>
                  )}
                  {isProviderTypeSelected && isSAMLAuth && !id && (
                    <CustomTextArea
                      label='Provider Signing Certificate'
                      name='providerSigningCertificate'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      rows='15'
                      required
                      disabled={modalStatus === 'View'}
                    />
                  )}
                  {/* {['Edit', 'Add'].includes(modalStatus) && ( */}
                  <>
                    <div className='grid grid-cols-8 justify-center items-center gap-2'>
                      <InputText
                        className='col-span-7'
                        label='Redirect URI'
                        name='redirectUri'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        required
                        disabled={true}
                      />
                      <Button
                        type='button'
                        btnClassName='col-span-1 mt-3'
                        title=''
                        icon='content_copy'
                        variant='primary'
                        onClick={() => {
                          onCopyToClipboard(values.redirectUri);
                        }}
                      />
                    </div>
                    <div className='grid grid-cols-8 justify-center items-center gap-2'>
                      <InputText
                        className='col-span-7'
                        label='Login Initiate URL'
                        name='loginInitiateUrl'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        required
                        disabled={true}
                      />
                      <Button
                        type='button'
                        btnClassName='col-span-1 mt-3'
                        title=''
                        icon='content_copy'
                        variant='primary'
                        onClick={() => {
                          onCopyToClipboard(values.loginInitiateUrl);
                        }}
                      />
                    </div>
                    <InputText
                      label='Config Identifier'
                      name='configIdentifier'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      required
                      disabled={true}
                    />
                  </>
                  {/* )} */}
                </ModalBodyComponent>
                <ModalFooterComponent
                  modalStatus={modalStatus}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  valueChanged={_.isEqual(values, initialValues)}
                  loading={isLoadingPostCompanySso || isLoadingPutCompanySso}
                  onEditModal={onEditModal}
                  urlName='sso-configuration'
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default SsoConfigurationModal;
